import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Archive, Download, FileArchive } from "lucide-react";
import { shortDateTime } from "@/lib/format";
import { useState } from "react";
import { toast } from "sonner";

function toCsv(rows: any[], headers: string[]) {
  const escape = (v: any) => `"${String(v ?? "").replaceAll('"','""')}"`;
  const body = rows.map(r => headers.map(h => escape(typeof r[h] === "object" ? JSON.stringify(r[h]) : r[h])).join(","));
  return [headers.join(","), ...body].join("\n");
}
function downloadFile(name: string, content: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

type AuditRow = { id: string; actor_id: string | null; action: string; entity_type: string; entity_id: string | null; metadata: Record<string, unknown> | null; created_at: string };

export function AuditPanel({ schoolId }: { schoolId: string }) {
  const { data: policies = [] } = useQuery<any[]>({
    queryKey: ["retention"], enabled: !!schoolId,
    queryFn: async () => (await supabase.from("data_retention_policies" as any).select("*").order("entity_type")).data ?? [],
  });
  const { data: events = [] } = useQuery<any[]>({
    queryKey: ["procurement-audit", schoolId], enabled: !!schoolId,
    queryFn: async () => (await supabase.from("procurement_audit_logs" as any).select("*").order("created_at", { ascending: false }).limit(30)).data ?? [],
  });
  const { data: queue = [] } = useQuery<any[]>({
    queryKey: ["maker-checker"], enabled: !!schoolId,
    queryFn: async () => (await supabase.from("maker_checker_queue" as any).select("*").order("created_at", { ascending: false }).limit(20)).data ?? [],
  });
  const { data: audit = [] } = useQuery<AuditRow[]>({
    queryKey: ["compliance-audit", schoolId], enabled: !!schoolId,
    queryFn: async () => (await supabase.from("audit_logs" as any)
      .select("id,actor_id,action,entity_type,entity_id,metadata,created_at")
      .in("action", ["demo.seeded","demo.reseeded","invitation.redeemed","invitation.revoked","merchant.approved","merchant.rejected"])
      .order("created_at", { ascending: false }).limit(200)).data as unknown as AuditRow[] ?? [],
  });

  const exportCsv = () => {
    const headers = ["timestamp","action","entity_type","entity_id","actor_id","metadata"];
    const rows = audit.map(r => [
      r.created_at, r.action, r.entity_type, r.entity_id ?? "", r.actor_id ?? "",
      JSON.stringify(r.metadata ?? {}).replaceAll('"','""'),
    ]);
    const csv = [headers, ...rows].map(row => row.map(c => `"${String(c)}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `compliance-audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const today = new Date().toISOString().slice(0,10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0,10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  const bundle = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase.rpc as any)("export_compliance_bundle", {
        _school_id: schoolId, _from: from, _to: to,
      });
      if (error) throw new Error(error.message);
      return data as { settlements: any[]; reconciliation: any[]; audit_trail: any[]; ledger: any[] };
    },
    onSuccess: (b) => {
      const stamp = `${from}_${to}`;
      downloadFile(`settlements-${stamp}.csv`, toCsv(b.settlements, ["batch_date","reference","gross_ugx","fee_ugx","net_ugx","tx_count","status","merchant_id"]));
      downloadFile(`reconciliation-${stamp}.csv`, toCsv(b.reconciliation, ["created_at","matched_prn","status","note","collection_id"]));
      downloadFile(`audit-trail-${stamp}.csv`, toCsv(b.audit_trail, ["created_at","action","entity_type","entity_id","actor_id","metadata"]));
      downloadFile(`ledger-${stamp}.csv`, toCsv(b.ledger, ["created_at","direction","amount_ugx","kind","title","ref"]));
      downloadFile(`compliance-bundle-${stamp}.json`, JSON.stringify(b, null, 2), "application/json");
      toast.success(`Exported ${b.settlements.length + b.reconciliation.length + b.audit_trail.length + b.ledger.length} rows across 5 files`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-zinc-900 p-6 text-slate-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-300/70">Compliance · Uganda NPS Act</div>
            <div className="mt-1 font-display text-2xl">Audit trail &amp; 10-year retention</div>
            <div className="mt-1 text-sm text-slate-100/80">Every wallet, procurement, demo-reseed and invitation event is retained for the statutory period.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="h-8 w-[140px] bg-slate-50 text-slate-900" />
            <Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="h-8 w-[140px] bg-slate-50 text-slate-900" />
            <Button size="sm" onClick={() => bundle.mutate()} disabled={bundle.isPending} className="gap-1 bg-emerald-500 text-emerald-950 hover:bg-emerald-400">
              <FileArchive className="h-3 w-3" />{bundle.isPending ? "Bundling…" : "Reconciliation & audit bundle"}
            </Button>
            <Button variant="secondary" size="sm" onClick={exportCsv} className="gap-1"><Download className="h-3 w-3" />Events CSV</Button>
            <Badge className="bg-slate-50 text-slate-900"><ShieldCheck className="mr-1 h-3 w-3" />Retention active</Badge>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-2 font-display text-base flex items-center gap-2"><Archive className="h-4 w-4" />Retention policies</div>
          <div className="space-y-1">
            {policies.map((p: any) => (
              <div key={p.id} className="grid grid-cols-3 items-center gap-2 rounded-md border p-2 text-sm">
                <div className="font-medium capitalize">{p.entity_type.replace(/_/g, ' ')}</div>
                <div className="text-xs text-muted-foreground">{p.legal_basis}</div>
                <Badge variant="outline" className="justify-self-end">{p.retention_years}y</Badge>
              </div>
            ))}
            {!policies.length && <div className="p-4 text-center text-xs text-muted-foreground">No policies configured</div>}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-2 font-display text-base">Maker-checker queue</div>
          <div className="space-y-1">
            {queue.map((q: any) => (
              <div key={q.id} className="grid grid-cols-4 items-center gap-2 rounded-md border p-2 text-sm">
                <div className="font-medium capitalize">{q.action_type.replace(/_/g, ' ')}</div>
                <div className="text-xs text-muted-foreground">{q.entity_type}</div>
                <div className="text-xs">{shortDateTime(q.created_at)}</div>
                <Badge variant="outline" className="justify-self-end capitalize">{q.status}</Badge>
              </div>
            ))}
            {!queue.length && <div className="p-4 text-center text-xs text-muted-foreground">Queue empty — nothing awaiting approval</div>}
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-display text-base">Demo-reseed & invitation audit</div>
          <span className="text-[11px] text-muted-foreground">{audit.length} events</span>
        </div>
        <div className="space-y-1 max-h-96 overflow-auto">
          {audit.map((e) => (
            <div key={e.id} className="grid grid-cols-[130px_180px_1fr_120px] items-center gap-2 rounded-md border p-2 text-xs">
              <div className="text-muted-foreground">{shortDateTime(e.created_at)}</div>
              <Badge variant="outline" className="justify-self-start capitalize">{e.action}</Badge>
              <div className="truncate font-mono text-[10px] text-muted-foreground">{JSON.stringify(e.metadata ?? {})}</div>
              <div className="text-[10px] font-mono text-muted-foreground truncate">{e.actor_id?.slice(0,8)}</div>
            </div>
          ))}
          {!audit.length && <div className="p-4 text-center text-xs text-muted-foreground">No compliance events yet.</div>}
        </div>
      </Card>

      <Card className="p-4">
        <div className="mb-2 font-display text-base">Recent procurement events</div>
        <div className="space-y-1">
          {events.map((e: any) => (
            <div key={e.id} className="grid grid-cols-4 items-center gap-2 rounded-md border p-2 text-sm">
              <div className="font-medium capitalize">{e.action}</div>
              <div className="text-xs text-muted-foreground">{e.entity_type}</div>
              <div className="text-[11px] font-mono text-muted-foreground">{e.entity_id?.slice(0, 8)}</div>
              <div className="text-xs">{shortDateTime(e.created_at)}</div>
            </div>
          ))}
          {!events.length && <div className="p-4 text-center text-xs text-muted-foreground">No events yet</div>}
        </div>
      </Card>
    </div>
  );
}
